import Loading from '@/pages/intelligence/components/Loading';
import EllipsisTextBubble from '@gp/components/EllipsisTextBubble';
import { LANGUAGE_EN, SYSTEM_THEME_DARK } from '@home/_config/config';
import { getName } from '@home/utils/common';
import { ThemeContext } from '@home/wrappers/common';
import classNames from 'classnames';
import { sortBy } from 'lodash-es';
import React, { FC, useContext, useMemo, useState } from 'react';
import { Col, Row, Tabs } from 'tea-component';
import { getLocale, useIntl } from 'umi';
import { useAgentModelsData } from '../../../AgentCollection/hooks';
import Change from '../../../svg/change-blue.svg';
import ChangeGrey from '../../../svg/change-grey.svg';
import AnnotationDots from '../../../svg/annotation-dots.svg';
import { useTabTemplate } from '../../hooks/useTabTemplate';
import type { IQAInputRef } from '../../../components/QAInput/types';
import './index.scss';

interface IRecommendProps {
  className?: string;
  /** 外部传入的推荐问题分类数据（可选，传入时优先使用） */
  dataSource?: any[];
  /** 外部传入的 loading 状态（可选，配合 dataSource 使用） */
  dataLoading?: boolean;
  /** 是否显示标题 */
  showTitle?: boolean;
  /** 跳转聊天页面时追加的 template 参数（如 'aiExcel'） */
  template?: string;
  /** 走 QAInput 现有提交链路（与手动发送一致，自动携带 Skill / Chart） */
  qaInputRef: React.RefObject<IQAInputRef>;
}

const Recommend: FC<IRecommendProps> = (props) => {
  const { className = '', dataSource, dataLoading, showTitle = false, template, qaInputRef } = props;
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const isEN = locale === LANGUAGE_EN;
  const { categoryList, selectedAgentId, loading: internalLoading } = useAgentModelsData();

  // 优先使用外部传入的数据源
  const effectiveCategoryList = dataSource ?? categoryList;
  const loading = dataLoading ?? internalLoading;
  const { themeValue } = useContext(ThemeContext);
  const isDark = themeValue === SYSTEM_THEME_DARK;
  // hover时也显示渐变色的icon
  const [hoveredTabId, setHoveredTabId] = useState<string | null>(null);
  // 排序后的分类数据
  const sortedCategories = useMemo(() => {
    const list = effectiveCategoryList?.map((row, index) => ({
      ...row,
      id: (index + 1).toString(),
    }));
    return sortBy(list, ['sort']);
  }, [effectiveCategoryList]);

  const {
    activeId: activeCategoryId, // 当前激活的分类ID
    tabs: categoryTabs, // 所有分类标签配置数组
    onTabChange: onCategoryChange, // 分类切换回调函数
  } = useTabTemplate({
    tabs: sortedCategories,
    storageKey: `__dbb_agent_home_recommend__${selectedAgentId}`
  });

  // 当分类数量 ≤ 1 时，隐藏分类标签
  const shouldShowCategories = sortedCategories?.length > 1;

  // 分类标签渲染配置
  const categoryTabList = useMemo(
    () =>
      categoryTabs?.map((category) => {
        const isActive = category.id === activeCategoryId;
        const isHovered = category.id === hoveredTabId;
        const shouldShowActiveIcon = isActive || isHovered;

        return {
          ...category,
          label: (
            <div
              className="dba-tab-label-wrapper"
              onMouseEnter={() => setHoveredTabId(category.id)}
              onMouseLeave={() => setHoveredTabId(null)}
            >
              {shouldShowActiveIcon ? (
                <img
                  src={isDark ? category?.activeIconDark : category?.activeIcon}
                  className='dba-tabs-bar-icons'
                  alt={`${category?.label} icon`}
                />
              ) : (
                <img src={category?.defaultIcon} className="dba-tabs-bar-icons" />
              )}
              <span className="dba-tabs-bar-text">
                {getName(isEN, category?.categoryNameEn, category?.categoryNameCn)}
              </span>
            </div>
          ),
        };
      }),
    [activeCategoryId, hoveredTabId, categoryTabs, isEN, isDark],
  );

  // 当前激活分类的问题列表
  const activeQuestions = useMemo(
    () => {
      // 如果只有一个分类或没有分类，直接返回所有问题
      if (sortedCategories?.length <= 1) {
        return sortedCategories?.flatMap(category => category.data || []) || [];
      }
      // 多分类时，返回当前选中分类的问题
      return categoryTabs?.find((category) => category.id === activeCategoryId)?.data;
    },
    [activeCategoryId, categoryTabs, sortedCategories],
  );

  return (
    <div className={classNames('dba-recommend', className)}>
      {shouldShowCategories ? (
        <>
          {showTitle && <div className="dba-rec-title">
            <img src={AnnotationDots} alt="recommend" className="dba-rec-title-icon" />
            <span className="dba-rec-title-text">
              {formatMessage({ id: 'dba_copilot_recommend' })}
            </span>
          </div>}
          <div className="dba-rec-tabs">
            <Tabs
              tabs={categoryTabList}
              className="dba-tabs-bar"
              onActive={(tab) => {
                onCategoryChange(tab.id);
              }}
              activeId={activeCategoryId}
            />
          </div>
        </>
      ) : (
        <div className="dba-rec-title">
          <img src={AnnotationDots} alt="recommend" className="dba-rec-title-icon" />
          <span className="dba-rec-title-text">
            {formatMessage({ id: 'dba_copilot_recommend' })}
          </span>
        </div>
      )}
      <div>
        <QuestionList
          key={activeCategoryId}
          {...{
            questions: activeQuestions,
            loading,
            template,
            qaInputRef,
          }}
        />
      </div>
    </div>
  );
};

export default Recommend;

// 推荐问题列表组件
export const QuestionList = ({
  questions,
  loading,
  template,
  qaInputRef,
}: {
  questions: any;
  loading: boolean;
  template?: string;
  qaInputRef: React.RefObject<IQAInputRef>;
}) => {
  const [currentBatchIndex, setCurrentBatchIndex] = useState(0);
  const [isHover, setIshover] = useState(false);
  const locale = getLocale();
  const { formatMessage } = useIntl();
  const isEN = locale === LANGUAGE_EN;
  const BATCH_SIZE = 6;

  // 每次截取BATCH_SIZE个问题
  const { visibleQuestions, shouldShowRefreshButton } = useMemo(() => {
    if (!questions || questions.length === 0)
      return { visibleQuestions: [], shouldShowRefreshButton: false };

    // 计算当前批次的问题
    const endIndex = currentBatchIndex + BATCH_SIZE;
    let batchQuestions = [];

    // 如果剩余问题不足6个，从开头补充
    if (endIndex > questions.length) {
      const remaining = endIndex - questions.length;
      batchQuestions = [...questions.slice(currentBatchIndex), ...questions.slice(0, remaining)];
    } else {
      batchQuestions = questions.slice(currentBatchIndex, endIndex);
    }

    return {
      visibleQuestions: batchQuestions,
      shouldShowRefreshButton: questions.length > BATCH_SIZE,
    };
  }, [questions, currentBatchIndex]);

  const handleRefreshQuestions = () => {
    // 计算下一个起始位置，如果到底则从头开始
    setCurrentBatchIndex((prevIndex) => {
      const nextIndex = prevIndex + BATCH_SIZE;
      return nextIndex >= questions.length ? 0 : nextIndex;
    });
  };

  /** 推荐问题点击：走 QAInput 提交流程，携带当前 Skill / Chart 后跳转 Chat */
  const handleQuestionClick = (question: { questionEn: string; questionCn: string; }) => {
    const questionText = getName(isEN, question.questionEn, question.questionCn);
    qaInputRef.current?.submitText(questionText, {
      from: 'homeQuestion',
      template,
    });
  };


  if (loading) return <Loading />;

  return (
    <div className="dba-recommend-container" key={currentBatchIndex}>
      <Row gap={20}>
        {visibleQuestions?.map((question: any) => {
          const questionText = getName(isEN, question.questionEn, question.questionCn);

          return (
            <Col span={8} key={question.value}>
              <div onClick={() => handleQuestionClick(question)}>
                <EllipsisTextBubble
                  className="dba-recommend-container-item"
                  ellipsisLineClamp={3}
                  text={questionText}
                >
                  {questionText}
                </EllipsisTextBubble>
              </div>
            </Col>
          );
        })}
        {shouldShowRefreshButton && (
          <div
            className="dba-recommend-container-item-refresh-icon"
            onClick={handleRefreshQuestions}
            onMouseEnter={() => setIshover(true)}
            onMouseLeave={() => setIshover(false)}
          >
            <img
              src={isHover ? Change : ChangeGrey}
              alt="change-icon"
              className="dba-change-icon"
            ></img>
            {formatMessage({ id: 'dba_change_list' })}
          </div>
        )}
      </Row>
    </div>
  );
};
